import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { App, Button, Card, Form, Input, Select, Space, Spin, Tag, Typography } from 'antd';
import { useState } from 'react';
import { useActiveOrg } from '@/features/organizations/active-org-context';
import {
  aiApi,
  AUDIO_PROVIDERS,
  CHAT_MODELS,
  CHAT_PROVIDERS,
  IMAGE_MODELS,
  IMAGE_PROVIDERS,
  TTS_PROVIDERS,
  type AudioProvider,
  type ChatProvider,
  type ImageProvider,
  type TtsProvider,
  type UpdateAiSettingsBody,
} from '../ai.api';

export function AiSettingsPage() {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const { activeOrg, activeOrgId } = useActiveOrg();
  const isAdmin = activeOrg?.role === 'owner' || activeOrg?.role === 'admin';

  const key = ['ai-settings', activeOrgId] as const;
  const query = useQuery({
    queryKey: key,
    queryFn: () => aiApi.getOrgSettings(activeOrgId ?? ''),
    enabled: Boolean(activeOrgId) && isAdmin,
  });

  const [provider, setProvider] = useState<ChatProvider | null>(null);
  const [imageProvider, setImageProvider] = useState<ImageProvider | null>(null);
  const [ttsProvider, setTtsProvider] = useState<TtsProvider | null>(null);
  const [audioProvider, setAudioProvider] = useState<AudioProvider | null>(null);
  const [model, setModel] = useState<string | null>(null);
  const [imageModel, setImageModel] = useState<string | null>(null);
  const [openaiKey, setOpenaiKey] = useState('');
  const [anthropicKey, setAnthropicKey] = useState('');
  const [googleKey, setGoogleKey] = useState('');
  const [elevenLabsKey, setElevenLabsKey] = useState('');

  const mutation = useMutation({
    mutationFn: (body: UpdateAiSettingsBody) => aiApi.updateOrgSettings(activeOrgId ?? '', body),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: key });
      setOpenaiKey('');
      setAnthropicKey('');
      setGoogleKey('');
      setElevenLabsKey('');
      message.success('AI settings saved');
    },
    onError: (e) => message.error(e instanceof Error ? e.message : 'Failed to save'),
  });

  if (!activeOrg) return <Typography.Paragraph>Select an organization first.</Typography.Paragraph>;
  if (!isAdmin) return <Typography.Paragraph>Admins only.</Typography.Paragraph>;
  if (query.isLoading || !query.data) return <Spin />;

  const settings = query.data;
  const currentProvider = provider ?? settings.chatProvider;
  const modelOptions = CHAT_MODELS[currentProvider];
  // When the saved model belongs to the other provider's family, show that
  // provider's default instead (mirrors the server's fallback).
  const savedModelValid = modelOptions.some((m) => m.value === settings.chatModel);
  const currentModel =
    model ?? (savedModelValid ? settings.chatModel : (modelOptions[0]?.value ?? ''));

  const currentImageProvider = imageProvider ?? settings.imageProvider;
  const imageModelOptions = IMAGE_MODELS[currentImageProvider];
  const savedImageModelValid = imageModelOptions.some((m) => m.value === settings.imageModel);
  const currentImageModel =
    imageModel ??
    (savedImageModelValid ? settings.imageModel : (imageModelOptions[0]?.value ?? ''));

  const currentTtsProvider = ttsProvider ?? settings.ttsProvider;
  const currentAudioProvider = audioProvider ?? settings.audioProvider;

  const save = () => {
    const body: UpdateAiSettingsBody = {
      chatProvider: currentProvider,
      chatModel: currentModel,
      imageProvider: currentImageProvider,
      imageModel: currentImageModel,
      ttsProvider: currentTtsProvider,
      audioProvider: currentAudioProvider,
    };
    if (openaiKey.trim()) body.openaiKey = openaiKey.trim();
    if (anthropicKey.trim()) body.anthropicKey = anthropicKey.trim();
    if (googleKey.trim()) body.googleKey = googleKey.trim();
    if (elevenLabsKey.trim()) body.elevenLabsKey = elevenLabsKey.trim();
    mutation.mutate(body);
  };

  return (
    <Space direction="vertical" size="large" style={{ width: '100%', maxWidth: 640 }}>
      <Typography.Title level={3} style={{ marginBottom: 0 }}>
        AI settings
      </Typography.Title>
      <Typography.Paragraph type="secondary">
        Choose the chat model provider and store API keys for {activeOrg.name}. Keys are encrypted
        at rest and never displayed again.
      </Typography.Paragraph>

      <Card>
        <Form layout="vertical">
          <Form.Item label="Chat provider">
            <Select<ChatProvider>
              value={currentProvider}
              onChange={(v) => {
                setProvider(v);
                // Model families differ per provider — re-pick from the new list.
                setModel(null);
              }}
              style={{ maxWidth: 260 }}
              options={CHAT_PROVIDERS}
            />
          </Form.Item>

          <Form.Item label="Model">
            <Select<string>
              value={currentModel}
              onChange={setModel}
              style={{ maxWidth: 420 }}
              options={modelOptions}
            />
          </Form.Item>

          <Form.Item
            label={
              <Space>
                Anthropic API key
                {settings.hasAnthropicKey ? <Tag color="green">Key set</Tag> : <Tag>Not set</Tag>}
              </Space>
            }
          >
            <Input.Password
              value={anthropicKey}
              onChange={(e) => setAnthropicKey(e.target.value)}
              placeholder={settings.hasAnthropicKey ? 'Leave blank to keep current' : 'sk-ant-…'}
              autoComplete="off"
            />
          </Form.Item>

          <Form.Item
            label={
              <Space>
                OpenAI API key
                {settings.hasOpenaiKey ? <Tag color="green">Key set</Tag> : <Tag>Not set</Tag>}
              </Space>
            }
          >
            <Input.Password
              value={openaiKey}
              onChange={(e) => setOpenaiKey(e.target.value)}
              placeholder={settings.hasOpenaiKey ? 'Leave blank to keep current' : 'sk-…'}
              autoComplete="off"
            />
          </Form.Item>

          <Typography.Title level={5} style={{ marginTop: 8 }}>
            Creative images
          </Typography.Title>
          <Form.Item
            label="Image provider"
            help="Google Gemini (Nano Banana) keeps your real product in the generated creative. OpenAI tends to redraw and alter product packaging. Cloudflare has a free daily allowance and is configured on the server, not here — it has no key field below."
          >
            <Select<ImageProvider>
              value={currentImageProvider}
              onChange={(v) => {
                setImageProvider(v);
                // Model families differ per provider — re-pick from the new list.
                setImageModel(null);
              }}
              style={{ maxWidth: 480 }}
              options={IMAGE_PROVIDERS}
            />
          </Form.Item>

          <Form.Item label="Image model">
            <Select<string>
              value={currentImageModel}
              onChange={setImageModel}
              style={{ maxWidth: 480 }}
              options={imageModelOptions}
            />
          </Form.Item>

          <Form.Item
            label={
              <Space>
                Google Gemini API key
                {settings.hasGoogleKey ? <Tag color="green">Key set</Tag> : <Tag>Not set</Tag>}
              </Space>
            }
          >
            <Input.Password
              value={googleKey}
              onChange={(e) => setGoogleKey(e.target.value)}
              placeholder={settings.hasGoogleKey ? 'Leave blank to keep current' : 'AIza…'}
              autoComplete="off"
            />
          </Form.Item>

          <Typography.Title level={5} style={{ marginTop: 8 }}>
            Narration (faceless video)
          </Typography.Title>
          <Form.Item
            label="Voice provider"
            help="Used to narrate faceless-video scripts. OpenAI reuses the OpenAI key; Google reuses the Gemini key; ElevenLabs needs its own key below."
          >
            <Select<TtsProvider>
              value={currentTtsProvider}
              onChange={setTtsProvider}
              style={{ maxWidth: 480 }}
              options={TTS_PROVIDERS}
            />
          </Form.Item>

          <Form.Item
            label={
              <Space>
                ElevenLabs API key
                {settings.hasElevenLabsKey ? <Tag color="green">Key set</Tag> : <Tag>Not set</Tag>}
              </Space>
            }
          >
            <Input.Password
              value={elevenLabsKey}
              onChange={(e) => setElevenLabsKey(e.target.value)}
              placeholder={settings.hasElevenLabsKey ? 'Leave blank to keep current' : 'sk_…'}
              autoComplete="off"
            />
          </Form.Item>

          <Form.Item
            label="Ambience & sound effects"
            help="Where rendered videos get ambience and sound effects. ElevenLabs generates them with AI (reuses the ElevenLabs key above); it automatically falls back to the local library if generation fails or no key is set."
          >
            <Select<AudioProvider>
              value={currentAudioProvider}
              onChange={setAudioProvider}
              style={{ maxWidth: 480 }}
              options={AUDIO_PROVIDERS}
            />
          </Form.Item>

          <Button type="primary" onClick={save} loading={mutation.isPending}>
            Save AI settings
          </Button>
        </Form>
      </Card>
    </Space>
  );
}
